/**
 * Formatting helpers used across the app.
 */

/**
 * Format a number as Indonesian Rupiah.
 * e.g. 3420000 → "Rp 3.420.000"
 */
export function formatRupiah(amount, showPlus = false) {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rp 0';
  const num = parseFloat(amount);
  const isNegative = num < 0;
  const absFormatted = Math.round(Math.abs(num)).toLocaleString('id-ID');
  if (isNegative) {
    return `-Rp ${absFormatted}`;
  }
  if (showPlus && num > 0) {
    return `+Rp ${absFormatted}`;
  }
  return `Rp ${absFormatted}`;
}

/**
 * Get the start of the week (Monday / Senin) for a given date.
 * Returns a Date object with time set to 00:00:00.
 */
export function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday
  const mondayOffset = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(mondayOffset);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Format a week_start date (Monday) to "17 — 23 Agu 2026" (Senin — Minggu)
 */
export function formatWeekRange(weekStartStr) {
  if (!weekStartStr) return '';
  const start = new Date(weekStartStr);
  const end = new Date(start);
  end.setDate(end.getDate() + 6); // Sunday

  const monthNames = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'];
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = monthNames[start.getMonth()];
  const endMonth = monthNames[end.getMonth()];
  const year = end.getFullYear();

  if (start.getMonth() === end.getMonth()) {
    return `${startDay} — ${endDay} ${endMonth} ${year}`;
  }
  return `${startDay} ${startMonth} — ${endDay} ${endMonth} ${year}`;
}

/**
 * Format date to YYYY-MM-DD (local, not UTC) for API params.
 */
export function toYMD(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Return emoji for a category based on its name (best-effort).
 */
const EMOJI_MAP = {
  makan: '🍜', minum: '☕', kopi: '☕', snack: '🍿', ngemil: '🍟',
  transport: '🚗', bensin: '⛽', ojek: '🛵', grab: '🚙',
  hiburan: '🎮', game: '🎯', nonton: '🎬', film: '🎬',
  belanja: '🛍️', baju: '👗', pakaian: '👕',
  tagihan: '📑', listrik: '💡', air: '💧', internet: '📡',
  kesehatan: '💊', dokter: '🏥', gym: '💪',
  pendidikan: '📚', kursus: '🎓',
  tabungan: '🏦', nabung: '💰', saving: '🏦', darurat: '🛡️',
  investasi: '📈', saham: '📈', reksa: '📊', emas: '🪙', deposito: '🏦',
  gaji: '💵', salary: '💵', income: '💰',
};

export function getCategoryEmoji(name = '') {
  const lower = name.toLowerCase();
  for (const [key, emoji] of Object.entries(EMOJI_MAP)) {
    if (lower.includes(key)) return emoji;
  }
  return '📦';
}

/**
 * Pick a CSS tone class (red/orange/yellow/blue) for category progress bars
 * based on spend percentage.
 */
export function getTone(percent) {
  if (percent >= 80) return 'red';
  if (percent >= 60) return 'orange';
  if (percent >= 40) return 'yellow';
  return 'blue';
}

/**
 * Repeat the 🌶️ emoji up to 5 times based on spice_level 1-10.
 */
export function renderPeppers(level) {
  const count = Math.round((level / 10) * 5);
  return '🌶️'.repeat(Math.max(1, count));
}

/**
 * Budget health label from score 0-100.
 */
export function healthLabel(score) {
  if (score >= 80) return 'AMAN 💚';
  if (score >= 60) return 'AMAN, TAPI...';
  if (score >= 40) return 'WASPADA ⚠️';
  return 'BAHAYA 🔴';
}
